import React, { useState } from 'react';
import { useTranslation } from 'next-i18next';

import Box from '@mui/material/Box';
import { Card, CardContent } from '@mui/material';

import Button from 'src/components/ui/Button';
import Typography from 'src/components/ui/Typography';
import Icon from 'src/components/ui/Icon';
import SuperposedAvatars from 'src/components/ui/SuperposedAvatars';
import { colours } from 'src/constants/colours';

import { useRecommendingUsersQuery } from 'src/hooks/__generated__/queries';
import type { CompanyProfileQuery, User } from 'src/types/__generated__/graphql';
import theme from 'src/utils/theme';

import type {
    ViewTypes,
    RecommendationModalOpenedType,
} from 'src/components/profile/recommendations/RecommendationsModal';
import RecommendationsModal from 'src/components/profile/recommendations/RecommendationsModal';
import { styledComponent, styledTag } from 'src/utils/styled';
import useDynamicLogEvents from 'src/hooks/useDynamicLogEvents';
import { AnalyticsEvent } from 'src/utils/analytics';

const StyledCard = styledComponent(Card)`
    background-color: ${theme.palette.primary.main};
`;

StyledCard.defaultProps = {
    variant: 'outlined',
};

const StyledCardContent = styledComponent(CardContent)`
    padding: 1rem 1.5rem;
    &.MuiCardContent-root:last-child {
        padding-bottom: 1rem;
    }
`;

const StyledText = styledComponent(Typography)`
    color: ${colours.grey.G50};

    @media screen and (max-width: 400px) {
        font-size: 0.85rem;
    }
`;

const IconContainer = styledTag('div')`
    border-radius: 2rem;
    width: 2rem;
    height: 2rem;
    background-color: ${colours.tertiary.main};
    display: flex;
    align-items: center;
    justify-content: center;
    position: absolute;
    bottom: 0;
    right: 0;
`;

interface Props {
    company: CompanyProfileQuery['companyProfile'];
}

const CompanyRecommendations: React.FC<Props> = ({ company }) => {
    const { t } = useTranslation(['profile', 'company']);
    const { logEventDynamic } = useDynamicLogEvents();

    const [recommendationModalOpened, setRecommendationModalType] = useState<RecommendationModalOpenedType>(null);
    const [selectedView, setSelectedView] = useState<ViewTypes>('recommendationsList');

    const { data } = useRecommendingUsersQuery({
        variables: {
            offset: 0,
            limit: 1,
            companyId: company.id,
        },
        fetchPolicy: 'cache-and-network',
    });

    return (
        <Box>
            <StyledCard>
                <StyledCardContent>
                    <Box display="flex" alignItems="center" justifyContent="space-between">
                        <Box display="flex" alignItems="center">
                            <Box
                                display="flex"
                                mr="0.75rem"
                                height={data?.recommendingUsers?.total ? '2.5rem' : '2rem'}
                                width={data?.recommendingUsers?.total ? '3rem' : '2rem'}
                                position="relative"
                            >
                                {data?.recommendingUsers?.total && data.recommendingUsers.total > 0 && (
                                    <SuperposedAvatars users={(data.recommendingUsers.results ?? []) as User[]} />
                                )}
                                <IconContainer>
                                    <Icon size="1rem" color={colours.white.main} name="ThumbsUpOutlined" />
                                </IconContainer>
                            </Box>
                            <StyledText>
                                <span
                                    dangerouslySetInnerHTML={{
                                        __html: t('company:recommendations', {
                                            count: company.companyAndMembersRecommendationsCount,
                                            plurial: company.companyAndMembersRecommendationsCount > 1 ? 's' : '',
                                        }),
                                    }}
                                />
                            </StyledText>
                        </Box>
                        <Button
                            variant="contained"
                            color="white"
                            onClick={() => {
                                logEventDynamic(AnalyticsEvent.SeeRecommendations);
                                setRecommendationModalType('fromRecommendations');
                            }}
                        >
                            {t('profile:seeCommonRelations')}
                        </Button>
                        {recommendationModalOpened && (
                            <RecommendationsModal
                                recommendationsCount={company.companyAndMembersRecommendationsCount}
                                modalOpenedType={recommendationModalOpened}
                                setModalType={setRecommendationModalType}
                                recommendationFromMe={company.recommendationFromMe}
                                setSelectedView={setSelectedView}
                                selectedView={selectedView}
                                company={company}
                            />
                        )}
                    </Box>
                </StyledCardContent>
            </StyledCard>
        </Box>
    );
};

export default CompanyRecommendations;
